import React from 'react';
import {
	ApLoaderDefault,
	ApButtonGroup,
	ApButtonMain,
	ApSearchDropdown,
} from '@alixpartners/ui-components';
import moment from 'moment';
import memoize from 'memoize-async';

import { PERMISSIONS } from 'contexts/PermissionsContext';
import {
	getClassificationOverrideRules,
	getTaxonomies,
	createUpdateClassificationOverrideRule,
	deleteUpdateClassificationOverrideRule,
} from 'utils/apiRequests';
import { DeleteIcon, EditIcon, CheckIcon, CloseIcon } from 'components/icons';

import { Section } from '../Section';

import {
	VendorsList,
	VendorsListHeader,
	VendorsListRow,
	VendorsListCell,
	NegativeVendorsListCell,
	Button,
	ButtonSave,
	ButtonDelete,
	ButtonCancel,
	ButtonText,
	ActiveList,
	ActiveRow,
	ActiveRowCell,
	ActiveRowCellTitle,
	ActiveRowCellValue,
	ModalBody,
	ApModal,
	ApButtonSecondary,
	SubsectionTitle,
} from './styles';

const memoizedApi = {
	getTaxonomies: memoize(getTaxonomies),
};

const compareOptions = (a, b) => {
	if (!a || !a.name || !b || !b.name || !a.name.localeCompare) {
		return 0;
	}

	return a.name.localeCompare(b.name);
};

export class VendorsSection extends React.Component {
	state = {
		isLoading: true,
		deletingRowIndex: null,
	};

	componentDidMount() {
		this.fetchData();
	}

	fetchData = async () => {
		const data = await getClassificationOverrideRules();
		const taxonomiesData = await memoizedApi.getTaxonomies();

		this.setState({
			rulesData: data.filter(overrideRule => !overrideRule.isDeleted),
			deletedRulesData: data.filter(overrideRule => overrideRule.isDeleted),
			taxonomies: taxonomiesData.taxonomies,
			isLoading: false,
		});
	};

	setActiveRowIndex = activeRowIndex => {
		if (this.state.editingRowIndex) {
			return;
		}

		if (this.state.activeRowIndex === activeRowIndex) {
			this.setState({ activeRowIndex: null });
			return;
		}

		this.setState({ activeRowIndex });
	};

	handleInputChange = value => {
		this.setState({ value, isFormValid: true });
	};

	handleSuggestionSelect = valueId => {
		this.setState({ valueId, isFormValid: true });
	};

	handleSave = async () => {
		const { rulesData, editingRowIndex, value, valueId } = this.state;
		const rule = rulesData[editingRowIndex];

		if (rule && value && valueId) {
			const requestData = {
				aP_L4Category: value,
				aP_L4Code: valueId,
				harmonizedVendorId: rule.harmonizedVendorId,
				harmonizedVendorName: rule.harmonizedVendorName,
				id: rule.id,
			};

			await createUpdateClassificationOverrideRule(requestData);
			this.fetchData();
		}

		this.resetState();
	};

	handleDelete = async () => {
		const { rulesData, deletingRowIndex } = this.state;
		const rule = rulesData[deletingRowIndex];

		if (rule) {
			this.resetState();
			this.setState({ isLoading: true });

			await deleteUpdateClassificationOverrideRule(rule);
			this.fetchData();
		}
	};

	resetState = () => {
		this.setState({
			value: null,
			valueId: null,
			editingRowIndex: null,
			deletingRowIndex: null,
		});
	};

	render() {
		const { animationIndex, permissions } = this.props;
		const {
			isLoading,
			rulesData,
			deletedRulesData,
			activeRowIndex,
			editingRowIndex,
			deletingRowIndex,
			taxonomies,
		} = this.state;

		const l4TaxonomiesOptions = (taxonomies || [])
			.map(taxonomy => ({
				name: taxonomy.aP_L4Category,
				value: taxonomy.aP_L4Code,
			}))
			.sort(compareOptions);

		return (
			<React.Fragment>
				<Section title={'Override rules'} animationIndex={animationIndex}>
					{isLoading && <ApLoaderDefault />}
					{!isLoading && rulesData && rulesData.length === 0 ? (
						<div>No override rules added</div>
					) : (
						<VendorsList>
							<VendorsListHeader>
								<VendorsListCell>Vendor</VendorsListCell>
								<VendorsListCell>Override category</VendorsListCell>
								<VendorsListCell />
							</VendorsListHeader>
							{rulesData &&
								rulesData.map((overrideRule, idx) => (
									<React.Fragment
										key={overrideRule.harmonizedVendorId + '_' + idx}
									>
										<VendorsListRow
											isActive={idx === activeRowIndex}
											isDeleting={idx === deletingRowIndex}
											isEditing={idx === editingRowIndex}
											onClick={() => this.setActiveRowIndex(idx)}
										>
											<VendorsListCell>
												{overrideRule.harmonizedVendorName}
											</VendorsListCell>
											<VendorsListCell
												onClick={
													idx === editingRowIndex
														? e => e.stopPropagation()
														: null
												}
											>
												{idx === editingRowIndex ? (
													<ApSearchDropdown
														options={l4TaxonomiesOptions || []}
														value={overrideRule.aP_L4Category}
														placeholder="Enter taxonomy"
														onInputChange={this.handleInputChange}
														onSuggestionSelected={this.handleSuggestionSelect}
														allowNewOption={false}
													/>
												) : (
													overrideRule.aP_L4Category
												)}
											</VendorsListCell>

											{permissions.includes(PERMISSIONS.CCOWrite) ? (
												<VendorsListCell>
													{idx === editingRowIndex ? (
														<React.Fragment>
															<ButtonSave
																onClick={async e => {
																	e.preventDefault();
																	e.stopPropagation();

																	await this.handleSave();
																}}
															>
																<CheckIcon width={24} height={24} />
																<ButtonText>Save</ButtonText>
															</ButtonSave>
															<ButtonCancel
																onClick={e => {
																	e.preventDefault();
																	e.stopPropagation();

																	this.setState({ editingRowIndex: null });
																}}
															>
																<CloseIcon width={24} height={24} />
																<ButtonText>Cancel</ButtonText>
															</ButtonCancel>
														</React.Fragment>
													) : (
														<React.Fragment>
															<Button
																onClick={e => {
																	e.preventDefault();
																	e.stopPropagation();

																	this.setState({ editingRowIndex: idx });
																}}
															>
																<EditIcon width={24} height={24} />
																<ButtonText>Edit</ButtonText>
															</Button>
															<ButtonDelete
																onClick={async e => {
																	e.preventDefault();
																	e.stopPropagation();
																	this.setState({ deletingRowIndex: idx });
																}}
															>
																<DeleteIcon width={24} height={24} />
																<ButtonText>Delete</ButtonText>
															</ButtonDelete>
														</React.Fragment>
													)}
												</VendorsListCell>
											) : (
												<VendorsListCell />
											)}
										</VendorsListRow>

										{idx === activeRowIndex && (
											<ActiveList isDeleting={idx === deletingRowIndex}>
												{overrideRule.logs.map((overrideRuleLog, logIndex) => (
													<ActiveRow key={overrideRuleLog.id + '_' + logIndex}>
														<ActiveRowCell>
															<ActiveRowCellTitle>Date</ActiveRowCellTitle>
															<ActiveRowCellValue>
																{moment(overrideRuleLog.date).format(
																	'D MMM YYYY HH:mm'
																)}
															</ActiveRowCellValue>
														</ActiveRowCell>
														<ActiveRowCell>
															<ActiveRowCellTitle>
																Changed from
															</ActiveRowCellTitle>
															<ActiveRowCellValue>
																{overrideRuleLog.changedFromAP_L4Category}
															</ActiveRowCellValue>
														</ActiveRowCell>
														<ActiveRowCell>
															<ActiveRowCellTitle>
																Changed to
															</ActiveRowCellTitle>
															<ActiveRowCellValue>
																{overrideRuleLog.changedToAP_L4Category}
															</ActiveRowCellValue>
														</ActiveRowCell>
														<ActiveRowCell>
															<ActiveRowCellTitle>
																Changed by
															</ActiveRowCellTitle>
															<ActiveRowCellValue>
																{overrideRuleLog.changedBy}
															</ActiveRowCellValue>
														</ActiveRowCell>
													</ActiveRow>
												))}
											</ActiveList>
										)}
									</React.Fragment>
								))}
						</VendorsList>
					)}
					<SubsectionTitle>Deleted override rules</SubsectionTitle>
					{!isLoading && deletedRulesData && deletedRulesData.length === 0 ? (
						<div>No deleted override rules</div>
					) : (
						<VendorsList>
							<VendorsListHeader negativeList>
								<NegativeVendorsListCell>Vendor</NegativeVendorsListCell>
								<NegativeVendorsListCell>
									Override category
								</NegativeVendorsListCell>
								<NegativeVendorsListCell>Date Deleted</NegativeVendorsListCell>
								<NegativeVendorsListCell>Deleted by</NegativeVendorsListCell>
							</VendorsListHeader>
							{deletedRulesData &&
								deletedRulesData.map((overrideRule, idx) => (
									<VendorsListRow
										key={overrideRule.harmonizedVendorId + '_' + idx}
										negativeList
									>
										<NegativeVendorsListCell>
											{overrideRule.harmonizedVendorName}
										</NegativeVendorsListCell>
										<NegativeVendorsListCell>
											{overrideRule.aP_L4Category}
										</NegativeVendorsListCell>
										<NegativeVendorsListCell>
											{moment(overrideRule.date).format('D MMM YYYY HH:mm')}
										</NegativeVendorsListCell>
										<NegativeVendorsListCell>
											{overrideRule.userFullName}
										</NegativeVendorsListCell>
									</VendorsListRow>
								))}
						</VendorsList>
					)}
				</Section>
				{deletingRowIndex !== null && (
					<DeleteConfirmationModal
						onConfirm={this.handleDelete}
						onClose={this.resetState}
					/>
				)}
			</React.Fragment>
		);
	}
}

export default VendorsSection;

export const DeleteConfirmationModal = ({ onConfirm, onClose }) => {
	return (
		<ApModal
			isOpen
			hasClosed={onClose}
			width={480}
			footer={() => (
				<ApButtonGroup justifyContent="flex-end">
					<ApButtonSecondary onClick={onClose}>Cancel</ApButtonSecondary>
					<ApButtonMain onClick={onConfirm}>Confirm</ApButtonMain>
				</ApButtonGroup>
			)}
		>
			<ModalBody>
				<span>
					You are about to delete the override rule for this vendor. Are you
					sure?
				</span>
			</ModalBody>
		</ApModal>
	);
};
